import { cn } from '@heidi/utils';
import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import type { ApprovalRequestInput } from '../projects/components/ApprovalRequestBlock.display';
import type { ResolveInlineApproval } from '../projects/components/inline-approval/useInlineApprovalResolve';
import { useInlineApprovalResolve } from '../projects/components/inline-approval/useInlineApprovalResolve';
import { InlineApprovalActions } from './inline-approval-actions';
import { blocksOfKind, type ApprovalPresentation } from './tool-call-presentation';

interface EhrSessionCreateApprovalBlockProps {
  approval: ApprovalRequestInput;
  presentation: ApprovalPresentation;
  onResolve: ResolveInlineApproval;
  /** Leading mark. The app passes the Heidi brand icon. */
  mark?: ReactNode;
}

/**
 * Slim "Heidi wants to…" banner for creating a Heidi session from an EHR
 * appointment. Reads the server's title, else names the EHR from its `ehr`
 * block.
 */
export const EhrSessionCreateApprovalBlock = ({
  approval,
  presentation,
  onResolve,
  mark,
}: EhrSessionCreateApprovalBlockProps) => {
  const intl = useIntl();
  const ehrName = blocksOfKind(presentation.blocks, 'ehr')[0]?.display_name.trim();
  const { pendingVerdict, isBusy, submit } = useInlineApprovalResolve({
    approvalId: approval.approval_id,
    onResolve,
  });
  const title =
    presentation.title ??
    (ehrName
      ? intl.formatMessage(
          {
            defaultMessage:
              'Heidi wants to create a new session from your {ehr} appointments',
            id: '8JZ7VzBvJe',
          },
          { ehr: ehrName }
        )
      : intl.formatMessage({
          defaultMessage:
            'Heidi wants to create a new session from your EHR appointments',
          id: '07MiTGDJnk',
        }));

  return (
    <div
      className={cn(
        'flex w-full items-center gap-2 rounded-xl border border-border bg-surface px-3 py-2.5',
        'duration-300 animate-in fade-in slide-in-from-bottom-1'
      )}
      data-testid={`ehr-session-create-approval-${approval.approval_id}`}
    >
      {mark}
      <p className="min-w-0 flex-1 break-words text-sm font-medium leading-snug text-text-primary [overflow-wrap:anywhere]">
        {title}
      </p>
      <div className="shrink-0">
        <InlineApprovalActions
          onApprove={() => submit('approve')}
          onDeny={() => submit('deny')}
          pendingVerdict={pendingVerdict}
          disabled={isBusy}
        />
      </div>
    </div>
  );
};
